function plainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

const LAUNCH_COUNTRIES = new Set(["US", "CA", "AU", "GB", "DE", "FR", "IN", "JP"]);
const MAX_BRIEF_PROFILES = 8;

function own(source, key) {
  return Object.prototype.hasOwnProperty.call(source, key);
}

function trimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function marketCountry(value) {
  const normalized = trimmedString(value).toUpperCase();
  return LAUNCH_COUNTRIES.has(normalized) ? normalized : null;
}

function distinctStrings(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const entry of value) {
    const normalized = trimmedString(entry).toLowerCase();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

function canonicalLength(profile) {
  if (profile?.preferredLength === "quick" || profile?.depth === "focused") return "quick";
  if (profile?.preferredLength === "deep" || profile?.depth === "deep") return "deep";
  return "balanced";
}

function canonicalTone(profile) {
  if (profile?.tone === "direct" || profile?.tone === "executive") return "executive";
  if (profile?.tone === "coach" || profile?.tone === "coaching") return "coaching";
  if (profile?.tone === "analytical") return "analytical";
  return "calm";
}

function canonicalPurpose(value) {
  const supported = new Set([
    "daily_clarity",
    "executive",
    "deep_focus",
    "life_admin",
    "learning",
    "markets",
    "sports",
    "custom",
  ]);
  return supported.has(value) ? value : "daily_clarity";
}

function canonicalTimeOfDay(value) {
  return ["morning", "midday", "evening", "anytime"].includes(value) ? value : "morning";
}

function briefProfileForAndroid(profile) {
  const source = plainObject(profile);
  if (!source) return null;
  const id = trimmedString(source.id) || "web-default";
  const result = {
    id,
    name: trimmedString(source.name) || "Daily Brief",
    sectionIds: distinctStrings(source.sectionIds),
    disabledSectionIds: distinctStrings(source.disabledSectionIds),
    purpose: canonicalPurpose(source.purpose),
    preferredLength: canonicalLength(source),
    tone: canonicalTone(source),
    timeOfDay: canonicalTimeOfDay(source.timeOfDay),
    templateId: trimmedString(source.templateId),
    narrationEnabled: source.narrationEnabled === true,
    languageCode: trimmedString(source.languageCode),
  };
  if (Number.isFinite(source.createdAtEpochMs) && source.createdAtEpochMs >= 0) {
    result.createdAtEpochMs = source.createdAtEpochMs;
  }
  if (Number.isFinite(source.updatedAtEpochMs) && source.updatedAtEpochMs >= 0) {
    result.updatedAtEpochMs = source.updatedAtEpochMs;
  }
  return result;
}

function parsedBriefProfiles(value) {
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((entry) => plainObject(entry)).slice(0, MAX_BRIEF_PROFILES)
      : [];
  } catch {
    return [];
  }
}

function mergedBriefProfilesJson(existingJson, profile) {
  const wireProfile = briefProfileForAndroid(profile);
  if (!wireProfile) return "";
  const profiles = parsedBriefProfiles(existingJson);
  const index = profiles.findIndex((entry) => trimmedString(entry.id) === wireProfile.id);
  if (index >= 0) {
    profiles[index] = { ...profiles[index], ...wireProfile };
  } else if (profiles.length < MAX_BRIEF_PROFILES) {
    profiles.push({
      createdAtEpochMs: 0,
      updatedAtEpochMs: 0,
      ...wireProfile,
    });
  } else {
    profiles[MAX_BRIEF_PROFILES - 1] = {
      createdAtEpochMs: 0,
      updatedAtEpochMs: 0,
      ...wireProfile,
    };
  }
  return JSON.stringify(profiles);
}

function visibleHomeCarouselIds(settings) {
  const source = plainObject(settings);
  if (!source || !Array.isArray(source.order)) return null;
  const hidden = plainObject(source.hidden) || {};
  return distinctStrings(source.order).filter((id) => hidden[id] !== true);
}

export function withWebAccountSettings(preferences, briefProfile, homeCarouselSettings) {
  const source = plainObject(preferences) || {};
  const profile = plainObject(briefProfile);
  const carousel = plainObject(homeCarouselSettings);
  const resolvedMarketCountry =
    marketCountry(source.marketCountry) ||
    marketCountry(source.country) ||
    marketCountry(profile?.marketCountry) ||
    "US";
  const briefProfilesJson = mergedBriefProfilesJson(source.briefProfilesJson, profile);
  const activeBriefProfileId =
    trimmedString(profile?.id) || trimmedString(source.activeBriefProfileId) || "web-default";
  const canonicalVisibleIds = visibleHomeCarouselIds(carousel);
  return {
    ...source,
    marketCountry: resolvedMarketCountry,
    briefProfilesJson,
    activeBriefProfileId,
    homeCarouselVisibleIds: canonicalVisibleIds,
    webBriefProfile: profile,
    webHomeCarouselSettings: carousel,
  };
}

export function readWebAccountSettings(preferences) {
  const source = plainObject(preferences) || {};
  return {
    marketCountry: marketCountry(source.marketCountry),
    briefProfilesJson:
      typeof source.briefProfilesJson === "string" ? source.briefProfilesJson : null,
    activeBriefProfileId:
      typeof source.activeBriefProfileId === "string" ? source.activeBriefProfileId.trim() : null,
    hasCanonicalHomeCarouselVisibleIds: own(source, "homeCarouselVisibleIds"),
    homeCarouselVisibleIds: own(source, "homeCarouselVisibleIds")
      ? source.homeCarouselVisibleIds
      : undefined,
    briefProfile: plainObject(source.webBriefProfile),
    homeCarouselSettings: plainObject(source.webHomeCarouselSettings),
  };
}
